import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type CatalogueSubmissionRow, type SubmissionCandidate } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { may } from '@/lib/may';
import { useSession } from '@/lib/session';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

const TABS = [
  ['pending', 'Waiting'],
  ['approved', 'Approved'],
  ['rejected', 'Rejected'],
  ['all', 'All'],
] as const;

/**
 * Pharmacies proposing packs the catalogue does not have.
 *
 * ── WHY MERGING IS THE EASY PATH ────────────────────────────────────────────
 *
 * Every submission arrives beside everything the matcher thinks it might
 * already be, strongest evidence first. Approving a fourth "Amoxil 500mg"
 * therefore means ignoring three matches sitting on the screen.
 *
 * That is deliberate. A catalogue with one row per pack is the entire reason a
 * product page can show eighteen pharmacies at once; four rows means four offer
 * ladders and a buyer who sees a quarter of the market on each.
 *
 * ── WHAT A CURATOR DECIDES THAT A SELLER NEVER COULD ────────────────────────
 *
 * Prescription-only, controlled, cold chain, and the category. They gate the
 * pharmacist check, the capture policy and the commission rate — a seller
 * marking their own product over-the-counter would route a prescription
 * medicine straight past a pharmacist.
 */
export function SubmissionsScreen() {
  const [status, setStatus] = useState<string>('pending');
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'submissions', status],
    queryFn: ({ signal }) => api.catalogueSubmissions(status, signal),
    staleTime: 0,
  });

  if (isError) {
    return <AdminError title="We couldn't load the queue" error={error} onRetry={() => refetch()} />;
  }

  const rows = data?.submissions ?? [];

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">
          Catalogue requests
        </h1>
        <p className="mt-1 max-w-[70ch] text-[13px] leading-relaxed text-muted">
          Packs pharmacies have in stock that ChiCare does not list yet. Merge into a product we
          already have wherever you can — one row per pack is what lets a product page show every
          pharmacy selling it.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setStatus(key);
              setOpen(null);
            }}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-medium',
              status === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
            {data?.counts?.[key] !== undefined ? (
              <span className="ml-1.5 opacity-70">{data.counts[key]}</span>
            ) : null}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} data-skeleton className="h-[86px] w-full" radius="card" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={status === 'pending' ? 'Nothing waiting' : `No ${status} requests`}
          body={
            status === 'pending'
              ? 'Requests appear here when a pharmacy has stock we do not carry yet.'
              : undefined
          }
          action={
            <Button variant="grey" onClick={() => setStatus('pending')}>
              Back to waiting
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <SubmissionCard
              key={row.reference}
              row={row}
              open={open === row.reference}
              onToggle={() => setOpen(open === row.reference ? null : row.reference)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionCard({
  row,
  open,
  onToggle,
}: {
  row: CatalogueSubmissionRow;
  open: boolean;
  onToggle: () => void;
}) {
  const waiting = row.status === 'pending';

  return (
    <Card className={cn('overflow-hidden p-0', open && 'ring-1 ring-blue')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-col gap-2 p-4 text-left sm:flex-row sm:items-center"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12px] font-medium text-ink">{row.reference}</span>
            {!waiting ? <Badge tone="neutral">{row.status}</Badge> : null}
            {row.matched_product ? <Badge tone="caution">Possible duplicate</Badge> : null}
          </div>

          <p className="m-0 mt-1.5 text-[14px] font-semibold text-ink">
            {[row.brand_name || row.generic_name, row.strength].filter(Boolean).join(' ')}
          </p>
          <p className="m-0 mt-0.5 text-[12.5px] text-muted">
            {[row.form, row.pack_size, row.manufacturer].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>

        <div className="shrink-0 text-[12px] text-muted sm:text-right">
          <div>{row.pharmacy}</div>
          <div className="text-faint">
            {row.filed_at ? new Date(row.filed_at).toLocaleDateString('en-NG') : ''}
          </div>
        </div>
      </button>

      {open ? <SubmissionDetail reference={row.reference} /> : null}
    </Card>
  );
}

function SubmissionDetail({ reference }: { reference: string }) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'submission', reference],
    queryFn: ({ signal }) => api.catalogueSubmission(reference, signal),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'submissions'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'submission', reference] });
  };

  if (isError) {
    return (
      <div className="border-t border-line p-4">
        <AdminError title="We couldn't load this request" error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-3 border-t border-line p-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const { submission, candidates } = data;
  const decided = submission.status !== 'pending';

  return (
    <div className="border-t border-line bg-paper p-4">
      {/* Which shop they filed this against, before anything else.
          It is a CLAIM, not a fact — the category the curator picks below is
          what actually decides — but a curator who does not read it first can
          approve a poultry vaccine into "Antibiotics" and put it on the human
          shelf, and nothing downstream would catch that. */}
      {submission.sector === 'veterinary' ? (
        <p
          data-testid="submission-sector"
          className="m-0 mb-3 rounded-card border border-blue bg-white px-3.5 py-2.5"
        >
          <span className="block font-mono text-[10px] uppercase tracking-[0.14em] text-blue">
            Filed as animal health
          </span>
          <span className="mt-0.5 block text-[12.5px] leading-relaxed text-ink">
            File it under a veterinary category, or it lands on the human shelf.
            {submission.species.length > 0
              ? ` They say it is for: ${submission.species.join(', ')}.`
              : ' They did not say which animals.'}
          </span>
        </p>
      ) : null}

      <dl className="mb-4 grid grid-cols-1 gap-x-6 gap-y-2.5 text-[12.5px] sm:grid-cols-2">
        <Fact label="NAFDAC number" value={submission.nafdac_reg_no ?? 'Not given'} mono />
        <Fact label="Barcode" value={submission.gtin ?? 'Not given'} mono />
        <Fact label="Their price" value={`₦${(submission.price_kobo / 100).toLocaleString('en-NG')}`} mono />
        <Fact label="Batch" value={`${submission.batch_no ?? '—'} · exp ${submission.expiry_date ?? '—'}`} mono />
        <Fact label="Quantity" value={String(submission.quantity)} />
        <Fact label="Sent by" value={submission.submitted_by ?? '—'} />
      </dl>

      {submission.note ? (
        <section className="mb-4">
          <h3 className="m-0 mb-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            What they said
          </h3>
          <p className="m-0 max-w-[68ch] text-[13.5px] leading-relaxed text-ink">{submission.note}</p>
        </section>
      ) : null}

      {submission.photo_count > 0 ? (
        <PackPhotos reference={reference} count={submission.photo_count} />
      ) : null}

      <Candidates candidates={candidates} />

      {decided ? (
        <div className="rounded-card border border-line bg-white p-3.5">
          <p className="m-0 text-[13px] text-ink">
            <b className="capitalize">{submission.status}</b>
            {submission.decision_note ? ` — ${submission.decision_note}` : ''}
          </p>
        </div>
      ) : (
        <DecisionPanel reference={reference} candidates={candidates} onDecided={refresh} />
      )}
    </div>
  );
}

/**
 * What the catalogue already has that this might be.
 *
 * A code match is stated as certainty; a name match is offered as a question,
 * because "Amoxil 500mg tablet" and "Amoxil 500mg suspension" differ by one
 * word and are different medicines.
 */
function Candidates({ candidates }: { candidates: SubmissionCandidate[] }) {
  if (candidates.length === 0) {
    return (
      <section className="mb-4">
        <h3 className="m-0 mb-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
          Nothing like it in the catalogue
        </h3>
        <p className="m-0 text-[13px] text-muted">This looks genuinely new.</p>
      </section>
    );
  }

  return (
    <section className="mb-4">
      <h3 className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        Might already be
      </h3>

      <div className="overflow-hidden rounded-card border border-line bg-white">
        {candidates.map((candidate) => (
          <div
            key={candidate.product.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-2 px-3.5 py-2.5 last:border-b-0"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[13.5px] font-semibold text-ink">
                {candidate.product.display_name}
              </span>
              <span className="block text-[12px] text-muted">
                {candidate.reasons.join(' · ')}
              </span>
            </span>

            {/* A code matched but the product is in the other shop. A barcode
                is globally unique, so this is either a miskeyed code or a
                product filed wrong — and it is the single most useful thing
                this screen can tell a curator, so it outranks the score. */}
            {candidate.crosses_sector ? (
              <Badge tone="caution">Other shop — check</Badge>
            ) : candidate.certain ? (
              <Badge tone="verified">Same pack</Badge>
            ) : (
              <span className="font-mono text-[11px] text-faint">{candidate.score}</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function DecisionPanel({
  reference,
  candidates,
  onDecided,
}: {
  reference: string;
  candidates: SubmissionCandidate[];
  onDecided: () => void;
}) {
  const { user } = useSession();

  // Only a same-shop certain match preselects. A cross-wall code collision is
  // never `certain` server-side either, but relying on one guard for something
  // this consequential is how the guard gets loosened later by somebody who
  // does not know what it was for.
  const [mergeInto, setMergeInto] = useState<number | null>(
    candidates.find((c) => c.certain && !c.crosses_sector)?.product.id ?? null,
  );
  const [categoryId, setCategoryId] = useState('');
  const [isPom, setIsPom] = useState(false);
  const [isColdChain, setIsColdChain] = useState(false);
  const [note, setNote] = useState('');

  // Both sides of the wall. Picking the category IS deciding which shelf this
  // submission lands on, so the picker must be able to offer both.
  const categories = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: ({ signal }) => api.adminCategories(signal),
  });

  const approve = useMutation({
    mutationFn: () =>
      api.approveSubmission(reference, {
        product_id: mergeInto ?? undefined,
        category_id: mergeInto ? undefined : Number(categoryId) || undefined,
        is_pom: mergeInto ? undefined : isPom,
        is_cold_chain: mergeInto ? undefined : isColdChain,
        note: note || undefined,
      }),
    onSuccess: onDecided,
  });

  const reject = useMutation({
    mutationFn: () => api.rejectSubmission(reference, note),
    onSuccess: onDecided,
  });

  const error = (approve.error ?? reject.error) instanceof ApiError
    ? ((approve.error ?? reject.error) as ApiError)
    : null;

  return (
    <div className="rounded-card border border-line bg-white p-4">
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setMergeInto(null)}
          className={cn(
            'min-h-11 rounded-btn border px-3.5 text-[13px] font-semibold',
            mergeInto === null ? 'border-blue bg-blue text-white' : 'border-line bg-white text-muted',
          )}
        >
          Add as new
        </button>

        {candidates.map((candidate) => (
          <button
            key={candidate.product.id}
            type="button"
            onClick={() => setMergeInto(candidate.product.id)}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-semibold',
              mergeInto === candidate.product.id
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted',
            )}
          >
            Merge into {candidate.product.display_name}
          </button>
        ))}
      </div>

      {mergeInto === null ? (
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-medium text-ink">Category</span>
            <select
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              className="min-h-12 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
            >
              <option value="">Choose one — it sets the commission rate</option>
              {(categories.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.sector === 'veterinary' ? 'Animal · ' : 'Human · '}
                  {category.path}
                </option>
              ))}
            </select>
          </label>

          {/* Never taken from the submission: these gate the pharmacist check
              and the capture policy. */}
          <Toggle label="Prescription only" checked={isPom} onChange={setIsPom} />
          <Toggle label="Cold chain" checked={isColdChain} onChange={setIsColdChain} />
        </div>
      ) : null}

      <label className="mt-4 block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
          Note {mergeInto === null ? '(optional)' : '(optional)'} — the pharmacy reads this
        </span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          className="w-full resize-y rounded-btn border border-line bg-white px-3 py-2.5 text-[14px] leading-relaxed text-ink outline-none focus:border-blue"
        />
      </label>

      {error ? (
        <p role="alert" className="m-0 mt-3 text-[12.5px] font-medium leading-relaxed text-danger">
          {error.message}
        </p>
      ) : null}

      {/* Reading the queue and deciding it are separate permissions, so a
          curator can be given the first without the second. Offering buttons
          that would come back 403 would make a granted role look broken. */}
      {may(user, 'submissions.decide') ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            loading={approve.isPending}
            disabled={mergeInto === null && !categoryId}
            onClick={() => approve.mutate()}
          >
            {mergeInto ? 'Merge and put on sale' : 'Add to the catalogue'}
          </Button>

          <Button
            variant="grey"
            loading={reject.isPending}
            disabled={note.trim().length < 10}
            onClick={() => reject.mutate()}
          >
            Reject
          </Button>
        </div>
      ) : (
        <p className="m-0 mt-4 rounded-card border border-line bg-paper px-3.5 py-2.5 text-[12.5px] leading-relaxed text-muted">
          Your role lets you read these requests but not decide them. Ask whoever manages your team
          if you need to approve or reject.
        </p>
      )}

      {note.trim().length < 10 ? (
        <p className="m-0 mt-2 text-[11.5px] text-faint">
          A rejection needs a note the pharmacy can act on.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The pack, as photographed on the counter.
 *
 * Private disk, so each is fetched with the token rather than sitting at a URL.
 * This is what the NAFDAC number gets checked against.
 */
function PackPhotos({ reference, count }: { reference: string; count: number }) {
  const [urls, setUrls] = useState<Array<string | null> | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all(
      Array.from({ length: count }, (_, index) =>
        api
          .submissionPhotoBlob(reference, index)
          .then((blob) => URL.createObjectURL(blob))
          .catch(() => null),
      ),
    ).then((loaded) => {
      if (cancelled) {
        loaded.forEach((url) => url && URL.revokeObjectURL(url));

        return;
      }

      setUrls(loaded);
    });

    return () => {
      cancelled = true;
      setUrls((current) => {
        current?.forEach((url) => url && URL.revokeObjectURL(url));

        return null;
      });
    };
  }, [reference, count]);

  return (
    <section className="mb-4">
      <h3 className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        The pack
      </h3>
      <ul className="m-0 grid list-none grid-cols-3 gap-2 p-0 sm:grid-cols-4 lg:grid-cols-6">
        {Array.from({ length: count }, (_, index) => {
          const url = urls?.[index];

          return (
            <li key={index} className="aspect-square overflow-hidden rounded-btn border border-line bg-white">
              {urls === null ? (
                <Skeleton className="h-full w-full" />
              ) : url ? (
                <a href={url} target="_blank" rel="noreferrer" className="block h-full w-full">
                  <img
                    data-testid="pack-photo"
                    src={url}
                    alt={`Pack photo ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </a>
              ) : (
                <span className="grid h-full place-items-center p-2 text-center text-[11px] text-muted">
                  Could not open
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="relative flex min-h-11 cursor-pointer items-center gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
      <span
        aria-hidden
        className={cn(
          'grid h-[19px] w-[19px] shrink-0 place-items-center rounded-[6px] border transition-colors',
          checked ? 'border-blue bg-blue text-white' : 'border-line bg-white',
          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue',
        )}
      >
        {checked ? (
          <svg
            viewBox="0 0 12 10"
            aria-hidden
            className="h-[9px] w-[11px] fill-none stroke-current"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1 5.2 4.3 8.5 11 1.6" />
          </svg>
        ) : null}
      </span>
      <span className="text-[13.5px] text-ink">{label}</span>
    </label>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 sm:block">
      <dt className="m-0 text-[11.5px] text-faint">{label}</dt>
      <dd className={cn('m-0 text-ink', mono && 'font-mono text-[12.5px]')}>{value}</dd>
    </div>
  );
}
